import { pb, escapeFilterValue } from '../lib/pocketbase'
import type { ComplianceItem, ComplianceStatus, ComplianceType } from '../types'
import { ensureUserAgency } from '../utils/ensureAgency'

const COMPLIANCE_COLLECTION = 'compliance_items'

export type CompliancePayload = {
  title: string
  description?: string | null
  type?: ComplianceType | null
  status?: ComplianceStatus
  due_date?: string | null
  completed_date?: string | null
  is_recurring?: boolean | null
  recurrence_pattern?: string | null
  reminder_days_before?: number | null
  notes?: string | null
  client_id?: string | null
  assigned_to?: string | null
}

function mapCompliance(record: Record<string, any>): ComplianceItem {
  return {
    id: record.id,
    title: record.title ?? '',
    description: record.description ?? null,
    type: record.type ?? null,
    status: record.status ?? 'pending',
    due_date: record.due_date ?? null,
    completed_date: record.completed_date ?? null,
    is_recurring: record.is_recurring ?? null,
    recurrence_pattern: record.recurrence_pattern ?? null,
    reminder_days_before: record.reminder_days_before ?? null,
    notes: record.notes ?? null,
    client_id: record.client_id ?? null,
    assigned_to: record.assigned_to ?? null,
    agency_id: record.agency_id ?? '',
    created_by: record.created_by ?? null,
    created: record.created ?? '',
    updated: record.updated ?? '',
  }
}

export async function listComplianceItems(opts?: {
  /** Only items whose due_date falls in [from, to] (ISO strings). The calendar's window. */
  dueBetween?: { from: string; to: string }
}) {

  // Get current user's agency for filtering
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  // If no agency, return empty - user needs to complete onboarding
  if (!agencyId) {
    return []
  }

  const filterParts = [`agency_id = "${escapeFilterValue(agencyId)}"`]
  if (opts?.dueBetween) {
    filterParts.push(`due_date >= "${escapeFilterValue(opts.dueBetween.from)}"`)
    filterParts.push(`due_date <= "${escapeFilterValue(opts.dueBetween.to)}"`)
  }

  const data = await pb
    .collection(COMPLIANCE_COLLECTION)
    .getFullList<Record<string, any>>({
      filter: filterParts.join(' && '),
      sort: '+due_date',
      batch: 500,
    })
  return data.map(mapCompliance)
}

export async function getComplianceItem(id: string) {
  const record = await pb.collection(COMPLIANCE_COLLECTION).getOne<Record<string, any>>(id)
  return mapCompliance(record)
}

export async function createComplianceItem(payload: CompliancePayload, creatorId?: string) {
  const user = pb.authStore.model
  if (!user) throw new Error('User not authenticated')

  const createPayload: any = { ...payload }

  // Set created_by to the provided creator ID or current user
  createPayload.created_by = creatorId || user.id

  // Ensure status has a default (required field)
  if (!createPayload.status) {
    createPayload.status = 'pending'
  }

  // Ensure user has an agency assigned (auto-create if needed)
  const userAgencyId = await ensureUserAgency()
  createPayload.agency_id = userAgencyId

  // Remove undefined/null/empty values (but keep false for booleans)
  Object.keys(createPayload).forEach(key => {
    const value = createPayload[key]
    if (value === undefined || value === null || value === '') {
      delete createPayload[key]
    }
  })

  const record = await pb.collection(COMPLIANCE_COLLECTION).create(createPayload)
  return mapCompliance(record)
}

export async function updateComplianceItem(id: string, payload: Partial<CompliancePayload>) {
  const record = await pb.collection(COMPLIANCE_COLLECTION).update(id, payload)
  return mapCompliance(record)
}

export async function deleteComplianceItem(id: string) {
  await pb.collection(COMPLIANCE_COLLECTION).delete(id)
}

export async function getUpcomingCompliance(limit = 5) {
  // Get current user's agency for filtering (security: agency isolation)
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  if (!agencyId) {
    return []
  }

  const today = new Date()
  const inSeven = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
  const data = await pb
    .collection(COMPLIANCE_COLLECTION)
    .getFullList<Record<string, any>>({
      filter: `agency_id = "${escapeFilterValue(agencyId)}"`,
      sort: '+due_date',
      batch: 500,
    })

  return data
    .map(mapCompliance)
    .filter(item => {
      if (!item.due_date) return false
      const due = new Date(item.due_date)
      return due >= today && due <= inSeven && item.status !== 'completed'
    })
    .slice(0, limit)
}

export async function listClientCompliance(
  clientId: string,
  options: { includeCompleted?: boolean } = {},
) {
  // Get current user's agency for filtering (security: agency isolation)
  const user = pb.authStore.model
  const agencyId = (user as any)?.agency_id

  if (!agencyId) {
    return []
  }

  const { includeCompleted = false } = options
  const baseFilter = `agency_id = "${escapeFilterValue(agencyId)}" && client_id = "${escapeFilterValue(clientId)}"`
  const filter = includeCompleted ? baseFilter : `${baseFilter} && status != "completed"`

  const data = await pb.collection(COMPLIANCE_COLLECTION).getFullList<Record<string, any>>({
    filter,
    sort: '+due_date',
    batch: 500,
  })
  return data.map(mapCompliance)
}
